import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Play, Pause } from 'lucide-react';

const HeroSection = () => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(true);
  const videoRef = useRef(null);

  const toggleVideo = () => {
    if (videoRef.current) {
      if (isVideoPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsVideoPlaying(!isVideoPlaying);
    }
  };

  const scrollToPrograms = () => {
    const element = document.querySelector('#programs');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="relative h-screen w-full overflow-hidden" data-testid="hero-section">
      {/* Video Background */}
      <video
        ref={videoRef}
        autoPlay
        muted
        loop
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
        poster="https://images.unsplash.com/photo-1737381508529-a110d717d5a7?q=85&w=1920&auto=format&fit=crop"
      >
        <source
          src="https://videos.pexels.com/video-files/4761434/4761434-uhd_2560_1440_24fps.mp4"
          type="video/mp4"
        />
      </video>

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-[#050505]" />

      {/* Video Control Button */}
      <button
        onClick={toggleVideo}
        className="absolute bottom-8 right-8 z-20 p-3 bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 transition-colors"
        data-testid="video-control-btn"
      >
        {isVideoPlaying ? <Pause size={20} className="text-white" /> : <Play size={20} className="text-white" />}
      </button>

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center px-4 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="max-w-5xl"
        >
          {/* Label */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-[#DC2626] text-sm md:text-base font-bold tracking-[0.3em] uppercase mb-6"
          >
            SDM School of Defense & Martial Arts
          </motion.p>

          {/* Main Headline */}
          <h1 className="font-['Oswald'] text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-white leading-[0.9] tracking-tighter uppercase mb-8">
            Train Like a Fighter.
            <br />
            <span className="text-[#DC2626]">Build Strength,</span>
            <br />
            Discipline, and Confidence.
          </h1>

          {/* Sub text */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="text-neutral-400 text-base md:text-lg max-w-2xl mx-auto mb-10"
          >
            Professional martial arts training in Mysuru. From beginners to fighters, 
            we build champions inside and outside the ring.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <button
              onClick={scrollToContact}
              className="btn-skew bg-[#DC2626] text-white font-bold uppercase tracking-wider px-8 py-4 hover:bg-[#B91C1C] transition-all animate-pulse-glow"
              data-testid="hero-join-btn"
            >
              <span>Join Training</span>
            </button>
            <button
              onClick={scrollToPrograms}
              className="btn-skew bg-transparent border-2 border-white text-white font-bold uppercase tracking-wider px-8 py-4 hover:bg-white hover:text-black transition-all"
              data-testid="hero-programs-btn"
            >
              <span>View Programs</span>
            </button>
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-12 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="cursor-pointer"
            onClick={() => {
              const element = document.querySelector('#about');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            <ChevronDown size={32} className="text-white/50 hover:text-white transition-colors" />
          </motion.div>
        </motion.div>
      </div>

      {/* Side decorative text */}
      <div className="absolute left-4 top-1/2 -translate-y-1/2 hidden lg:block">
        <p className="font-['Oswald'] text-[10px] tracking-[0.5em] text-white/20 uppercase transform -rotate-90 origin-center whitespace-nowrap">
          Mysuru's Premier MMA Academy
        </p>
      </div>
    </section>
  );
};

export default HeroSection;
